import React, { useMemo, useState } from 'react';
import { getApiErrorData } from '../services/apiError';
import type { WxbDataTableProps } from './wxb-ui';
import {
  WxbAlert,
  WxbButton,
  WxbDataTable,
  WxbKpiCard,
  WxbModal,
  WxbSpinner,
  WxbTag,
  WxbUpload,
  wxbToast,
} from './wxb-ui';
import { employeeQualificationApi } from '../services/api';
import {
  readQualificationMatrixWorkbook,
  type QualificationMatrixWorkbookCell,
  type QualificationMatrixWorkbookEmployee,
  type QualificationMatrixWorkbookPreview,
  type QualificationMatrixWorkbookQualification,
} from '../utils/qualificationMatrixWorkbook';
import './QualificationMatrixImportModal.css';

interface ExistingMatrixItem {
  employee_id: number;
  qualification_id: number;
  qualification_level: number;
}

interface QualificationMatrixImportModalProps {
  open: boolean;
  employees: QualificationMatrixWorkbookEmployee[];
  qualifications: QualificationMatrixWorkbookQualification[];
  matrixData: ExistingMatrixItem[];
  onClose: () => void;
  onImported: () => Promise<void> | void;
}

type ChangeAction = 'create' | 'update' | 'delete';

interface PreviewChange extends QualificationMatrixWorkbookCell {
  key: string;
  action: ChangeAction;
  currentLevel: number | null;
}

interface ImportPreview extends QualificationMatrixWorkbookPreview {
  changes: PreviewChange[];
  created: number;
  updated: number;
  deleted: number;
  unchanged: number;
}

const getMatrixCellKey = (employeeId: number, qualificationId: number): string =>
  `${employeeId}-${qualificationId}`;

const getErrorMessage = (error: unknown, fallback: string): string => {
  const data = getApiErrorData<{ error?: unknown; message?: unknown }>(error);
  const message = data?.error ?? data?.message;
  if (typeof message === 'string' && message.trim()) return message;
  return error instanceof Error && error.message ? error.message : fallback;
};

const buildImportPreview = (
  workbook: QualificationMatrixWorkbookPreview,
  matrixData: ExistingMatrixItem[],
): ImportPreview => {
  const currentLevelByCell = new Map(
    matrixData.map((item) => [
      getMatrixCellKey(item.employee_id, item.qualification_id),
      item.qualification_level,
    ]),
  );
  let created = 0;
  let updated = 0;
  let deleted = 0;
  let unchanged = 0;
  const changes: PreviewChange[] = [];

  workbook.cells.forEach((cell) => {
    const key = getMatrixCellKey(cell.employee_id, cell.qualification_id);
    const currentLevel = currentLevelByCell.get(key) ?? null;
    if (currentLevel === cell.qualification_level) {
      unchanged += 1;
      return;
    }

    let action: ChangeAction;
    if (cell.qualification_level === null) {
      action = 'delete';
      deleted += 1;
    } else if (currentLevel === null) {
      action = 'create';
      created += 1;
    } else {
      action = 'update';
      updated += 1;
    }
    changes.push({ ...cell, key, action, currentLevel });
  });

  return {
    ...workbook,
    changes,
    created,
    updated,
    deleted,
    unchanged,
  };
};

const actionMeta: Record<ChangeAction, { label: string; color: 'green' | 'blue' | 'red' }> = {
  create: { label: '新增', color: 'green' },
  update: { label: '改级', color: 'blue' },
  delete: { label: '移除', color: 'red' },
};

const QualificationMatrixImportModal: React.FC<QualificationMatrixImportModalProps> = ({
  open,
  employees,
  qualifications,
  matrixData,
  onClose,
  onImported,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [exportingDiff, setExportingDiff] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setErrorMessage(null);
  };

  const close = () => {
    if (previewing || importing) return;
    reset();
    onClose();
  };

  const runPreview = async (selected: File) => {
    setPreviewing(true);
    setErrorMessage(null);
    setPreview(null);
    try {
      const workbookPreview = await readQualificationMatrixWorkbook(selected, { employees, qualifications });
      setPreview(buildImportPreview(workbookPreview, matrixData));
    } catch (error) {
      setErrorMessage(getErrorMessage(error, '文件预检失败，请检查文件后重试'));
    } finally {
      setPreviewing(false);
    }
  };

  const handleFilesSelected = async (files: File[]) => {
    const selected = files[0];
    if (!selected || previewing || importing) return;
    setFile(selected);
    await runPreview(selected);
  };

  const handleDownloadTemplate = async () => {
    if (downloadingTemplate || !employees.length || !qualifications.length) return;
    setDownloadingTemplate(true);
    try {
      const { exportQualificationMatrixToExcel } = await import('../utils/exportQualificationMatrixExcel');
      exportQualificationMatrixToExcel({
        employees,
        qualifications,
        matrixData,
        filters: {
          showEmptyRows: true,
        },
        fileNamePrefix: '资质矩阵导入模板',
      });
      wxbToast.success('已下载当前资质矩阵模板');
    } catch {
      wxbToast.error('模板下载失败');
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleExportDiff = async () => {
    if (!preview || exportingDiff) return;
    setExportingDiff(true);
    try {
      const { exportQualificationMatrixDiffToExcel } = await import('../utils/exportQualificationMatrixExcel');
      exportQualificationMatrixDiffToExcel({
        sourceFileName: file?.name || '未命名文件',
        employeeCount: preview.employeeCount,
        qualificationCount: preview.qualificationCount,
        created: preview.created,
        updated: preview.updated,
        deleted: preview.deleted,
        unchanged: preview.unchanged,
        items: preview.changes.map((change) => ({
          action: change.action,
          employee_code: change.employee_code,
          employee_name: change.employee_name,
          qualification_id: change.qualification_id,
          qualification_name: change.qualification_name,
          current_level: change.currentLevel,
          imported_level: change.qualification_level,
        })),
      });
      wxbToast.success('已下载 Diff 总结');
    } catch {
      wxbToast.error('Diff 总结下载失败');
    } finally {
      setExportingDiff(false);
    }
  };

  const handleImport = async () => {
    if (!preview?.changes.length || importing) return;
    setImporting(true);
    try {
      const response = await employeeQualificationApi.batchUpdate(preview.changes.map((change) => ({
        employee_id: change.employee_id,
        qualification_id: change.qualification_id,
        qualification_level: change.qualification_level,
      })));
      await onImported();
      wxbToast.success(
        `导入完成：新增 ${response.data.created}，改级 ${response.data.updated}，移除 ${response.data.deleted}`,
      );
      reset();
      onClose();
    } catch (error) {
      setErrorMessage(getErrorMessage(error, '资质矩阵导入失败，数据未写入'));
    } finally {
      setImporting(false);
    }
  };

  const columns = useMemo<WxbDataTableProps<PreviewChange>['columns']>(() => [
    {
      title: '员工',
      key: 'employee',
      width: 180,
      render: (_: unknown, row: PreviewChange) => (
        <div className="qualification-import-modal__entity">
          <strong>{row.employee_name}</strong>
          <span>{row.employee_code}</span>
        </div>
      ),
    },
    { title: '资质', dataIndex: 'qualification_name', width: 260 },
    {
      title: '变更',
      dataIndex: 'action',
      width: 90,
      render: (action: ChangeAction) => (
        <WxbTag color={actionMeta[action].color}>{actionMeta[action].label}</WxbTag>
      ),
    },
    {
      title: '当前',
      dataIndex: 'currentLevel',
      width: 80,
      render: (level: number | null) => level ?? '—',
    },
    {
      title: '导入后',
      dataIndex: 'qualification_level',
      width: 90,
      render: (level: number | null) => level ?? '—',
    },
  ], []);

  const canImport = Boolean(preview?.changes.length) && !previewing && !importing;

  return (
    <WxbModal
      open={open}
      title="导入资质矩阵"
      width={900}
      onCancel={close}
      maskClosable={!previewing && !importing}
      closable={!previewing && !importing}
      footer={(
        <div className="qualification-import-modal__footer">
          <WxbButton type="button" variant="ghost" onClick={close} disabled={previewing || importing}>
            关闭
          </WxbButton>
          <WxbButton type="button" variant="primary" onClick={handleImport} disabled={!canImport}>
            {importing ? '正在导入…' : `确认导入${preview?.changes.length ? `（${preview.changes.length}）` : ''}`}
          </WxbButton>
        </div>
      )}
    >
      <div className="qualification-import-modal__body">
        <WxbAlert title="先下载当前矩阵模板">
          模板会预填现有等级并附带填写说明。修改 1-5 级数值后上传；空白或“-”会移除对应资质。
        </WxbAlert>

        <div className="qualification-import-modal__upload-heading">
          <div>
            <strong>选择文件并自动预检</strong>
            <span>仅支持不超过 5 MB 的 .xlsx 文件</span>
          </div>
          <div className="qualification-import-modal__heading-actions">
            <WxbButton
              type="button"
              variant="secondary"
              size="sm"
              onClick={handleDownloadTemplate}
              disabled={downloadingTemplate || previewing || importing || !employees.length || !qualifications.length}
            >
              {downloadingTemplate ? '生成中…' : '下载当前矩阵模板'}
            </WxbButton>
            {file && (
              <WxbButton type="button" variant="ghost" size="sm" onClick={reset} disabled={previewing || importing}>
                清除文件
              </WxbButton>
            )}
          </div>
        </div>

        <WxbUpload
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          multiple={false}
          onFilesSelected={handleFilesSelected}
          fileList={file ? [{
            uid: `${file.name}-${file.lastModified}`,
            name: file.name,
            size: file.size,
            status: previewing ? 'uploading' : errorMessage ? 'error' : 'done',
          }] : []}
        />

        {previewing && <WxbSpinner tip="正在预检资质矩阵…" />}
        {errorMessage && <WxbAlert variant="error" title="未能导入">{errorMessage}</WxbAlert>}

        {preview && !previewing && (
          <>
            <div className="qualification-import-modal__summary">
              <WxbKpiCard title="新增" value={preview.created} />
              <WxbKpiCard title="改级" value={preview.updated} />
              <WxbKpiCard title="移除" value={preview.deleted} />
              <WxbKpiCard title="不变" value={preview.unchanged} />
            </div>

            <div className="qualification-import-modal__preview-actions">
              <WxbButton
                type="button"
                variant="secondary"
                size="sm"
                onClick={handleExportDiff}
                disabled={exportingDiff || importing}
              >
                {exportingDiff ? '生成中…' : '下载 Diff 总结'}
              </WxbButton>
            </div>

            <WxbAlert
              variant={preview.changes.length ? 'warning' : 'error'}
              title={preview.changes.length ? '预检通过，可以确认导入' : '没有检测到变更'}
            >
              已检查 {preview.qualificationCount} 项资质、{preview.employeeCount} 名员工。
              {preview.deleted > 0 ? ` 确认后将移除 ${preview.deleted} 条资质记录。` : ''}
            </WxbAlert>

            {preview.changes.length > 0 && (
              <WxbDataTable<PreviewChange>
                rowKey="key"
                density="compact"
                columns={columns}
                dataSource={preview.changes}
                pagination={preview.changes.length > 10 ? {
                  pageSize: 10,
                  showSizeChanger: false,
                  showTotal: (total) => `共 ${total} 项变更`,
                } : false}
                scroll={{ x: 760, y: 300 }}
              />
            )}
          </>
        )}
      </div>
    </WxbModal>
  );
};

export default QualificationMatrixImportModal;
